import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { v4 as uuidv4 } from 'uuid';
import isEmpty from 'lodash/isEmpty';
import {
  blueVioletColor,
  goldColor,
  grayColor,
  navyColor,
  successColor,
  turquoiseColor,
  whiteColor,
} from 'assets/jss/material-dashboard-pro-react';
import { formatNumberWithThinSpaces } from 'helpers/helpers';
import EXTERNAL_URLS from 'constants/externalUrls';
import InfoWithTooltip from 'components/InfoWithTooltip/InfoWithTooltip';

import { SHORTEST_MONTH_LENGTH } from '../consts';
import getExternalTooltipHtml from './utils/getExternalTooltipHtml';
import getExternalTooltipPercentageFormattedValue from './utils/getExternalTooltipPercentageFormattedValue';
import setExternalTooltip from './utils/setExternalTooltip';
import getExternalTooltipDate from './utils/getExternalTooltipDate';
import UsersLegend from './UsersLegend';
import MetricsLegend from './MetricsLegend';
import NoData from './NoData/NoData';

ChartJS.register(
  BarElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Title
);

const chartColors = [
  goldColor,
  turquoiseColor,
  successColor[2],
  blueVioletColor,
];

const lineColors = ['#E66161', '#AA47D5', '#ED7505', '#871414'];

const tooltipId = uuidv4();

const DATE_SOURCES = {
  allClients: 'allClients',
  newClients: 'newClients',
  returningClients: 'returningClients',
};

const useStyles = makeStyles({
  tabRoot: {
    textTransform: 'inherit',
    textAlign: 'inherit',
    fontSize: '16px',
    fontWeight: '500',
    borderBottom: `1px solid ${grayColor[4]}`,
  },
  labelContainer: {
    padding: '8px 0 22px 14px',
  },
  indicator: {
    height: '4px',
  },
  indicatorColor0: {
    backgroundColor: chartColors[0],
  },
  indicatorColor1: {
    backgroundColor: chartColors[1],
  },
  indicatorColor2: {
    backgroundColor: chartColors[2],
  },
  indicatorColor3: {
    backgroundColor: chartColors[3],
  },
  chartWrapper: {
    position: 'relative',
    marginTop: '34px',
    height: '330px',
  },
  spinnerWrapper: {
    position: 'absolute',
    display: 'grid',
    placeItems: 'center',
    inset: '0',
  },
  legendContainer: {
    display: 'flex',
    alignItems: 'stretch',
    padding: '16px 10px',
  },
  totalValue: {
    fontSize: '20px',
    fontWeight: '700',
  },
  totalTitle: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '4px',
    fontSize: '16px',
    fontWeight: '500',
  },
  titleWrapper: {
    paddingRight: '20px',
    marginRight: '20px',
    borderRight: `1px solid ${grayColor[4]}`,
  },
  legend: {
    display: 'flex',
    justifyContent: 'space-around',
    flexDirection: 'column',
  },
  newClients: {
    fontSize: '12px',
    fontWeight: '600',
    '& span': {
      fontWeight: '400',
      color: grayColor[20],
    },
    '&::before': {
      content: '""',
      display: 'inline-block',
      width: '10px',
      height: '10px',
      backgroundColor: goldColor,
      marginRight: '8px',
    },
  },
  returningClients: {
    fontSize: '12px',
    fontWeight: '600',
    '& span': {
      fontWeight: '400',
      color: grayColor[20],
    },
    '&::before': {
      content: '""',
      display: 'inline-block',
      width: '10px',
      height: '10px',
      backgroundColor: navyColor,
      marginRight: '8px',
    },
  },
});

const UsersChart = ({ statistics, renderLabels, isLoading }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [firstStatistics, ...additionalStatistics] = statistics;

  if (isLoading) {
    return (
      <div className={classes.spinnerWrapper}>
        <CircularProgress />
      </div>
    );
  }

  if (isEmpty(firstStatistics)) {
    return null;
  }

  const hasFirstStatisticsDataToShow =
    firstStatistics.newClients.total !== 0 ||
    firstStatistics.returningClients.total !== 0;
  const hasAdditionalStatisticsDataToShow = !!(
    additionalStatistics.length > 0 &&
    additionalStatistics.some(
      ({ newClients, returningClients }) =>
        newClients.total !== 0 || returningClients.total !== 0
    )
  );
  const hasDataToShow =
    hasFirstStatisticsDataToShow || hasAdditionalStatisticsDataToShow;

  const returningClientsDataset =
    firstStatistics.returningClients.dataSeries.map(({ value }) => value);

  const newClientsDataset = firstStatistics.newClients.dataSeries.map(
    ({ value }) => value
  );

  const combinedMaxYValue = Math.max(
    ...firstStatistics.newClients.dataSeries.map((_, index) =>
      statistics.reduce((maxValue, stat) => {
        const newClients = stat.newClients?.dataSeries?.[index]?.value || 0;
        const returningClients =
          stat.returningClients?.dataSeries?.[index]?.value || 0;
        const totalForThisStat = newClients + returningClients;

        return Math.max(maxValue, totalForThisStat);
      }, 0)
    )
  );

  const totalReturningClients = firstStatistics.returningClients.total;
  const totalNewClients = firstStatistics.newClients.total;
  const totalClients = totalReturningClients + totalNewClients;

  const isSingleDataPoint = Object.values(firstStatistics).every(
    stat => stat.dataSeries.length === 1
  );

  const getLineDataset = (stats, color) => {
    if (
      !stats?.newClients?.dataSeries ||
      !stats?.returningClients?.dataSeries
    ) {
      return null;
    }

    const sumDataset = stats.newClients.dataSeries.map((item, index) => {
      const newClientsValue = item.value;
      const returningClientsValue =
        stats.returningClients.dataSeries?.[index]?.value;

      if (newClientsValue === null && returningClientsValue === null) {
        return null;
      }

      return newClientsValue + returningClientsValue;
    });

    const { dateFrom } = stats.newClients.dataSeries[0];
    const { dateTo } = stats.newClients.dataSeries.at(-1);

    const getTotal = () => {
      if (
        stats.returningClients.total === null ||
        stats.newClients.total === null
      ) {
        return null;
      }
      return stats.returningClients.total + stats.newClients.total;
    };

    return {
      type: 'line',
      dataSource: DATE_SOURCES.allClients,
      label: t('dashboard.clients.count'),
      tooltipTitle: t('dashboard.clients.count'),
      legendLabel: {
        datesRange: [dateFrom, dateTo],
        total: getTotal(),
      },
      getValueSuffix: count => t('dashboard.clients.count', { count }),
      data: sumDataset,
      borderColor: color,
      backgroundColor: color,
      yAxisID: 'yLines',
      tension: 0,
      pointRadius: isSingleDataPoint ? 7 : 0,
      hitRadius: 7,
      hoverRadius: 7,
      borderWidth: 3,
      datalabels: {
        display: false,
      },
      statisticsSource: {
        ...stats.newClients,
        total: getTotal(),
        dataSeries: stats.newClients.dataSeries.map((stat, index) => ({
          ...stat,
          value: stat.value + stats.returningClients.dataSeries[index].value,
        })),
      },
    };
  };

  const lineDatasets = additionalStatistics.map((stats, index) =>
    getLineDataset(stats, lineColors[index])
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: ({ chart, tooltip }) => {
          if (!tooltip.dataPoints) {
            return;
          }

          const [
            {
              dataIndex,
              raw,
              dataset: { backgroundColor, dataSource, statisticsSource },
            },
          ] = tooltip.dataPoints;

          const { dateFrom, dateTo } = statisticsSource.dataSeries[dataIndex];

          const allClientsCount =
            newClientsDataset[dataIndex] + returningClientsDataset[dataIndex];

          const getClientsCountText = () => {
            switch (dataSource) {
              case DATE_SOURCES.newClients:
                return t('dashboard.newClients.count', { count: raw });
              case DATE_SOURCES.returningClients:
                return t('dashboard.returningClients.count', { count: raw });
              default:
                return t('dashboard.clients.count', { count: raw });
            }
          };

          const getAdditionalContent = () =>
            dataSource === DATE_SOURCES.allClients
              ? [
                  {
                    highlightedText: raw,
                    text: getClientsCountText(),
                  },
                ]
              : [
                  {
                    highlightedText: getExternalTooltipPercentageFormattedValue(
                      {
                        raw,
                        total: allClientsCount,
                      }
                    ),
                    text: getClientsCountText(),
                  },
                  {
                    hasLineBefore: true,
                    highlightedText: allClientsCount,
                    text: t('dashboard.allClients.count', {
                      count: allClientsCount,
                    }),
                  },
                ];

          setExternalTooltip({
            tooltipId,
            chart,
            tooltip,
            tooltipInnerHTML: getExternalTooltipHtml({
              title: t('dashboard.clients.count'),
              color: backgroundColor,
              content: [
                {
                  text: getExternalTooltipDate(dateFrom, dateTo),
                },
                ...getAdditionalContent(),
              ],
            }),
          });
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        stacked: true,
        ...(statistics.length > 1 && { max: combinedMaxYValue }),
        grid: { drawBorder: false, color: grayColor[4], drawTicks: false },
        ticks: {
          color: grayColor[20],
          font: { size: 12, weight: 400 },
          callback: (value, index) =>
            index === 0 ? '' : formatNumberWithThinSpaces(value),
          padding: 6,
        },
      },
      yLines: {
        ...(statistics.length > 1 && { max: combinedMaxYValue }),
        beginAtZero: true,
        stacked: false,
        display: false,
      },
      x: {
        offset: true,
        stacked: true,
        grid: {
          drawOnChartArea: false,
          drawBorder: false,
          color: grayColor[4],
        },
        ticks: {
          color: grayColor[20],
          font: { size: 12, weight: 400 },
          padding: 4,
          maxRotation: 0,
          minRotation: 0,
          autoSkipPadding: 10,
        },
      },
    },
  };

  return (
    <>
      <div className={classes.legendContainer}>
        <div className={classes.titleWrapper}>
          <p className={classes.totalTitle}>
            {t('dashboard.clients.count')}
            <InfoWithTooltip
              title={t('dashboard.statisticsPanelClientsInfo')}
              link={EXTERNAL_URLS.statisticsPanelClients}
            />
          </p>
          <span className={classes.totalValue}>
            {formatNumberWithThinSpaces(totalClients)}
          </span>
        </div>
        <div className={classes.legend}>
          <UsersLegend
            className={classes.newClients}
            totalSum={totalClients}
            partialSum={totalNewClients}
            title={t('dashboard.clients.newClients')}
          />
          <UsersLegend
            className={classes.returningClients}
            totalSum={totalClients}
            partialSum={totalReturningClients}
            title={t('dashboard.clients.returningClients')}
          />
        </div>
      </div>
      {lineDatasets.length > 0 && <MetricsLegend datasets={lineDatasets} />}
      <div className={classes.chartWrapper}>
        <Bar
          data={{
            labels: firstStatistics.newClients.dataSeries.map(renderLabels),
            datasets: [
              ...lineDatasets,
              {
                dataSource: DATE_SOURCES.returningClients,
                label: t('dashboard.clients.returningClients'),
                data: returningClientsDataset,
                backgroundColor: navyColor,
                borderWidth: 0,
                barPercentage: 0.9,
                maxBarThickness: 80,
                datalabels: {
                  display:
                    returningClientsDataset.length < SHORTEST_MONTH_LENGTH,
                  color: whiteColor,
                  font: {
                    size: 12,
                    weight: 500,
                  },
                },
                statisticsSource: firstStatistics.returningClients,
              },
              {
                dataSource: DATE_SOURCES.newClients,
                label: t('dashboard.clients.newClients'),
                data: newClientsDataset,
                backgroundColor: goldColor,
                borderWidth: 0,
                barPercentage: 0.9,
                maxBarThickness: 80,
                datalabels: {
                  display: context => {
                    const isShortEnough =
                      newClientsDataset.length < SHORTEST_MONTH_LENGTH;
                    const currentValue =
                      context.dataset.data[context.dataIndex];
                    const isNotZero = currentValue !== 0;

                    return isShortEnough && isNotZero;
                  },
                  color: navyColor,
                  font: {
                    size: 12,
                    weight: 500,
                  },
                },
                statisticsSource: firstStatistics.newClients,
              },
            ],
          }}
          options={options}
        />
        {!hasDataToShow && <NoData />}
      </div>
    </>
  );
};

export default UsersChart;
